import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Tooltip,
  Checkbox,
  Divider,
  Box,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import LaunchIcon from "@mui/icons-material/Launch"; // View client portal
import ButtonComponent from "../../ui/ButtonComponent";
import { useNavigate } from "react-router-dom";

const formatPhoneNumber = (phoneString) => {
  const cleaned = ("" + (phoneString || "")).replace(/\D/g, "");
  if (cleaned.length !== 10) return phoneString || "";
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]})-${match[2]}-${match[3]}` : phoneString;
};

// Helper to normalize various timestamp shapes into a JS Date
const normalizeToDate = (ts) => {
  if (!ts) return null;

  // Firestore Timestamp (client SDK)
  if (typeof ts.toDate === "function") {
    try {
      return ts.toDate();
    } catch {
      // fall through
    }
  }

  // Firestore admin Timestamp JSON: { _seconds, _nanoseconds } or { seconds, nanoseconds }
  if (typeof ts === "object") {
    const seconds = ts._seconds ?? ts.seconds;
    if (typeof seconds === "number") {
      return new Date(seconds * 1000);
    }
  }

  // Numbers (ms since epoch)
  if (typeof ts === "number") {
    return new Date(ts);
  }

  // ISO strings
  if (typeof ts === "string") {
    const d = new Date(ts);
    if (!isNaN(d.getTime())) return d;
  }

  return null;
};

const formatStackedTimestamp = (timestamp) => {
  const date = normalizeToDate(timestamp);
  if (!date) return "Unknown";

  const dateString = date.toLocaleDateString();
  const timeString = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <>
      <div>{dateString}</div>
      <div style={{ fontSize: "0.8em", color: "#555" }}>{timeString}</div>
    </>
  );
};

/**
 * Elegant client card with:
 * - Client-level Actions menu (Assign Forms, Invite, Save bundle, Apply bundle, Select mode)
 * - "View client portal" (opens /portal with act-as query params)
 * - Per-doc overflow menu (View, Reassign, Remove)
 * - Select mode to multi-select documents and run "Assign selected documents…" (via callback)
 */
const ClientDocumentCard = ({
  client,
  onRefresh,
  isRefreshing,
  onHide,
  hideKey,
  onReassignClick,
  onRemoveRequest,
  onAssignForms,
  onInviteClient,
  onSaveFormsAsBundle,
  onApplyBundle,
  onAssignSelectedDocs,
  // NEW: shows subtle "Added" badge that auto-fades (animation defined in DocumentsDashboard wrapper)
  isRecentlyAdded,
}) => {
  const navigate = useNavigate();

  // per-document menu
  const [docMenuAnchor, setDocMenuAnchor] = useState(null);
  const [currentDoc, setCurrentDoc] = useState(null);

  // client-level menu
  const [actionsAnchor, setActionsAnchor] = useState(null);

  // selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);

  const handleOpenDocMenu = (event, doc) => {
    setDocMenuAnchor(event.currentTarget);
    setCurrentDoc(doc);
  };
  const handleCloseDocMenu = () => {
    setDocMenuAnchor(null);
    setCurrentDoc(null);
  };

  const handleOpenActions = (e) => setActionsAnchor(e.currentTarget);
  const handleCloseActions = () => setActionsAnchor(null);

  const handleRefresh = async () => {
    if (!client?.id) return;
    await onRefresh(client.id);
  };

  // Build a /portal URL that passes act-as context to the client portal app.
  const buildPortalUrl = () => {
    const origin = window.location.origin;
    const actAsClient = encodeURIComponent(client?.id || client?.clientId || "");
    const actAsOrg = encodeURIComponent(client?.orgId || client?.organizationId || "");
    const actAsName = encodeURIComponent(client?.fullName || "");
    const params = new URLSearchParams();
    if (actAsClient) params.set("actAsClient", actAsClient);
    if (actAsOrg) params.set("actAsOrg", actAsOrg);
    if (actAsName) params.set("actAsName", actAsName);
    const qs = params.toString();
    return `${origin}/portal${qs ? `?${qs}` : ""}`;
  };

  // NEW: submissions array from Firestore (multiple forms)
  const submissionDocs = Array.isArray(client.submissions)
    ? client.submissions.map((sub, idx) => {
        const ts = sub.submittedAt || sub.updatedAt || sub.createdAt || null;

        const formName =
          sub.formTitle || sub.title || sub.formName || sub.formId || "Form Submission";

        return {
          id: sub.id || `submission-${idx}`,
          name: formName,
          timestamp: ts,
          label: "Download Form",
          action: () =>
            window.open(sub.formPdfUrl || sub.pdfUrl || sub.downloadUrl || "#", "_blank"),
          isAssigned: false,
        };
      })
    : [];

  // Legacy single submission shape (keep for backwards compat)
  const legacyFormSubmission = client.formPdfUrl
    ? {
        id: `legacy-form-${client.formId || "submission"}`,
        name: client.formTitle || client.formName || client.formId || "Form Submission",
        timestamp: client.lastSubmissionDate || client.submissionDate || null,
        label: "Download Form",
        action: () => window.open(client.formPdfUrl, "_blank"),
        isAssigned: false,
      }
    : null;

  const annotatedPdf = client.annotatedPdfUrl
    ? {
        id: `annotated-${client.formId || "pdf"}`,
        name: client.annotatedPdfName || "Annotated PDF",
        timestamp: client.lastSubmissionDate || null,
        label: "Download Annotated",
        action: () => window.open(client.annotatedPdfUrl, "_blank"),
        isAssigned: false,
      }
    : null;

  const uploadedDocs = (client.documents || []).map((doc, idx) => ({
    id: doc.id || doc.docId || `uploaded-${idx}`,
    name: doc.name || "Unnamed Document",
    timestamp:
      doc.uploaded?._seconds || doc.uploaded?.seconds
        ? { _seconds: doc.uploaded._seconds ?? doc.uploaded.seconds }
        : null,
    label: doc.name?.includes("ClientReportCard") ? "Annotate" : "View",
    action: () =>
      doc.name?.includes("ClientReportCard")
        ? navigate("/documents/annotations", {
            state: { fileUrl: doc.url, name: doc.name, uploaded: doc.uploaded },
          })
        : window.open(doc.downloadUrl || "#", "_blank"),
    isAssigned: false,
  }));

  const assignedDocs = (client.assignedDocuments || []).map((doc, idx) => ({
    id: doc.docId || doc.id || `assigned-${idx}`,
    name: doc.name || "Assigned Document",
    timestamp: doc.originalUploadDate || doc.assignedAt || null,
    action: () => window.open(doc.downloadUrl || "#", "_blank"),
    isAssigned: true,
  }));

  const documents = useMemo(() => {
    const baseFromSubmissions = submissionDocs;
    const otherDocs = [annotatedPdf, ...uploadedDocs].filter(Boolean);

    if (baseFromSubmissions.length > 0) {
      return [...baseFromSubmissions, ...otherDocs];
    }

    return [legacyFormSubmission, ...otherDocs].filter(Boolean);
  }, [submissionDocs, legacyFormSubmission, annotatedPdf, uploadedDocs]);

  // Toggle select mode
  const toggleSelectMode = () => {
    setSelectMode((v) => !v);
    setSelectedDocIds([]);
    handleCloseActions();
  };

  const allSelectableRows = [...documents, ...assignedDocs];
  const allSelected =
    selectedDocIds.length > 0 && selectedDocIds.length === allSelectableRows.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(allSelectableRows.map((d) => d.id));
    }
  };

  const toggleRow = (rowId) => {
    setSelectedDocIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const runAssignSelectedDocs = () => {
    if (!onAssignSelectedDocs || selectedDocIds.length === 0) return;
    onAssignSelectedDocs({ client, docIds: selectedDocIds });
    setSelectedDocIds([]);
    setSelectMode(false);
  };

  return (
    <>
      <Card className="dashboard-card">
        <CardContent>
          {/* Header */}
          <div className="card-header" style={{ alignItems: "center" }}>
            <h2 style={{ marginRight: 8 }}>{client.fullName || "Unnamed Client"}</h2>

            {/* NEW: subtle "Added" badge (auto-fades via keyframes defined in DocumentsDashboard wrapper) */}
            {isRecentlyAdded && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: "0.70rem",
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  lineHeight: 1.4,
                  border: "1px solid rgba(0,0,0,0.12)",
                  backgroundColor: "rgba(63,81,181,0.08)",
                  color: "rgba(0,0,0,0.72)",
                  animation: "kinovaFadeOut 6s ease-in-out forwards",
                  userSelect: "none",
                }}
              >
                Added
              </Box>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {/* Select Mode toggle */}
              <Tooltip title={selectMode ? "Exit Select Mode" : "Enter Select Mode"}>
                <IconButton size="small" onClick={toggleSelectMode}>
                  <SelectAllIcon fontSize="small" color={selectMode ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>

              {/* Client-level actions */}
              <IconButton size="small" onClick={handleOpenActions}>
                <MoreVertIcon />
              </IconButton>

              <IconButton
                size="small"
                onClick={handleRefresh}
                color="primary"
                disabled={isRefreshing}
              >
                {isRefreshing ? <CircularProgress size={18} thickness={5} /> : <RefreshIcon />}
              </IconButton>

              <IconButton size="small" onClick={() => onHide(hideKey)}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          {/* Contact */}
          <p className="text-sm text-gray-500">{client.email || "No contact info"}</p>
          <p className="text-sm text-gray-500">
            {formatPhoneNumber(client.phone) || "No contact info"}
          </p>

          {/* Table */}
          <TableContainer style={{ maxHeight: 300, overflow: "auto", marginTop: "1rem" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {selectMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedDocIds.length > 0 &&
                          selectedDocIds.length < allSelectableRows.length
                        }
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Date Uploaded</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Action</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    {selectMode && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDocIds.includes(doc.id)}
                          onChange={() => toggleRow(doc.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Tooltip title={doc.name} placement="top">
                        <Typography variant="body2" noWrap sx={{ maxWidth: "175px" }}>
                          {doc.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatStackedTimestamp(doc.timestamp)}</TableCell>
                    <TableCell align="right">
                      <ButtonComponent
                        label={doc.label}
                        onClick={doc.action}
                        type="primary"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {assignedDocs.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell colSpan={selectMode ? 4 : 3} sx={{ py: 0 }}>
                        <Divider sx={{ my: 1 }} />
                      </TableCell>
                    </TableRow>

                    {assignedDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        {selectMode && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedDocIds.includes(doc.id)}
                              onChange={() => toggleRow(doc.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Tooltip title={doc.name} placement="top">
                            <Typography variant="body2" noWrap sx={{ maxWidth: "175px" }}>
                              {doc.name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatStackedTimestamp(doc.timestamp)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleOpenDocMenu(e, doc)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Select-mode footer bar */}
          {selectMode && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px dashed #e0e0e0",
                borderRadius: 1,
                p: 1,
                bgcolor: "#fafafa",
              }}
            >
              <Typography variant="body2">{selectedDocIds.length} selected</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <ButtonComponent
                  label="Assign selected documents…"
                  type="secondary"
                  size="small"
                  onClick={runAssignSelectedDocs}
                  disabled={!onAssignSelectedDocs || selectedDocIds.length === 0}
                />
                <ButtonComponent
                  label="Cancel"
                  type="secondary"
                  size="small"
                  onClick={() => {
                    setSelectedDocIds([]);
                    setSelectMode(false);
                  }}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Per-doc actions menu */}
      <Menu anchorEl={docMenuAnchor} open={Boolean(docMenuAnchor)} onClose={handleCloseDocMenu}>
        <MenuItem
          onClick={() => {
            if (currentDoc?.action) currentDoc.action();
            handleCloseDocMenu();
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">View</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onReassignClick(currentDoc);
            handleCloseDocMenu();
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Reassign</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (currentDoc?.id) onRemoveRequest(currentDoc.docId || currentDoc.id);
            handleCloseDocMenu();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Remove</Typography>
        </MenuItem>
      </Menu>

      {/* Client-level actions */}
      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={handleCloseActions}>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            onAssignForms && onAssignForms(client);
          }}
          disabled={!onAssignForms}
        >
          <ListItemIcon>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Assign forms…</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            onInviteClient && onInviteClient(client);
          }}
          disabled={!onInviteClient}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Invite to client portal…</Typography>
        </MenuItem>

        {/* View client portal (act-as) */}
        <MenuItem
          onClick={() => {
            handleCloseActions();
            const url = buildPortalUrl();
            window.open(url, "_blank", "noopener,noreferrer");
          }}
        >
          <ListItemIcon>
            <LaunchIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">View client portal</Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            handleCloseActions();
            onSaveFormsAsBundle && onSaveFormsAsBundle(client);
          }}
          disabled={!onSaveFormsAsBundle}
        >
          <ListItemIcon>
            <SaveIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Save current form selection as bundle…</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseActions();
            onApplyBundle && onApplyBundle(client);
          }}
          disabled={!onApplyBundle}
        >
          <ListItemIcon>
            <PlaylistAddIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Apply bundle…</Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={toggleSelectMode}>
          <ListItemIcon>
            <SelectAllIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            {selectMode ? "Exit select mode" : "Select documents…"}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ClientDocumentCard;